/**
 * Xamun Owner admin — full audit log.
 *
 * Read-only view onto xamun_admin_audit_log via
 * GET /api/admin/tenants/audit/list. Useful when you need to answer
 * "who flipped this flag?" or "when did Acme get provisioned?" without
 * pulling the audit table directly.
 *
 * Phase A: shows the most recent 100 entries with no pagination —
 * upgrade to cursor pagination once we have enough volume to need it.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ApiError} from "../../api/client";
import { apiGet } from "../../api/client";
import { AdminShell } from "../../components/admin/AdminShell";

interface AuditEntry {
  id: string;
  actorEmail: string;
  action: string;
  targetKind: string | null;
  targetId: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

const ACTION_TONE: Record<string, "approve" | "return" | "note"> = {
  "tenant.provision":         "approve",
  "tenant.suspend":           "return",
  "tenant.resume":            "approve",
  "tenant.archive":           "return",
  "flag.set":                 "note",
  "flag.delete":              "note",
  "invitation_code.mint":     "approve",
  "invitation_code.revoke":   "return",
  "invitation_code.redeem":   "approve",
};

export function AdminAudit() {
  const [actionFilter, setActionFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");

  const q = useQuery<{ entries: AuditEntry[]; total: number }, ApiError>({
    queryKey: ["admin-audit"],
    queryFn: () => apiGet("/api/admin/tenants/audit/list"),
    retry: false,
  });

  const filtered = useMemo(() => {
    if (!q.data) return [];
    return q.data.entries.filter((e) => {
      if (actionFilter && !e.action.includes(actionFilter.toLowerCase())) return false;
      if (actorFilter && !e.actorEmail.toLowerCase().includes(actorFilter.toLowerCase())) return false;
      return true;
    });
  }, [q.data, actionFilter, actorFilter]);

  // Distinct action values for the filter helper.
  const actionOptions = useMemo(() => {
    if (!q.data) return [] as string[];
    return Array.from(new Set(q.data.entries.map((e) => e.action))).sort();
  }, [q.data]);

  return (
    <AdminShell>
    <main className="review-page" style={{ margin: "0 auto" }}>
      <p className="review-eyebrow">xamun owner</p>
      <h1 className="review-title">Audit log</h1>
      <p className="review-meta">
        Every mutating <code>/admin</code> action and every invitation-code redemption
        writes here. Append-only; useful when something drifts and you need
        to know who did it.
      </p>

      {q.error?.status === 403 && (
        <div className="review-banner error" style={{ marginTop: 16 }}>
          You're not on the Xamun admin allow-list.
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
        }}
      >
        <input
          className="review-input"
          placeholder="Filter by actor email"
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
          style={{ flex: "1 1 200px" }}
        />
        <select
          className="review-input"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          style={{ flex: "0 0 220px" }}
        >
          <option value="">All actions</option>
          {actionOptions.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: "var(--text-3)" }}>
          {q.data ? `${filtered.length} of ${q.data.entries.length} entries` : ""}
        </span>
      </div>

      <section className="review-section">
        {q.isLoading ? (
          <div style={{ color: "var(--text-3)" }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="ho-empty">No matching audit entries.</div>
        ) : (
          <ul className="review-comment-list">
            {filtered.map((e) => {
              const tone = ACTION_TONE[e.action] ?? "note";
              return (
                <li key={e.id} className="review-comment">
                  <div className="review-comment-head">
                    <strong className="author">{e.action}</strong>
                    <span className={`review-comment-decision ${tone}`}>
                      {e.targetKind ?? "—"}
                    </span>
                    <span style={{ color: "var(--text-3)" }}>by {e.actorEmail}</span>
                    <span style={{ marginLeft: "auto", fontSize: 11 }}>
                      {new Date(e.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {e.targetId && e.targetKind === "workspace" ? (
                    <div style={{ marginTop: 6, fontSize: 12 }}>
                      Target:{" "}
                      <Link
                        to={`/admin/tenants/${e.targetId}`}
                        style={{
                          fontFamily: "monospace",
                          color: "var(--accent, #6366f1)",
                        }}
                      >
                        {e.targetId}
                      </Link>
                    </div>
                  ) : e.targetId ? (
                    <div style={{ marginTop: 6, fontSize: 12 }}>
                      Target:{" "}
                      <code style={{ fontFamily: "monospace" }}>{e.targetId}</code>
                    </div>
                  ) : null}
                  {Object.keys(e.payload).length > 0 && (
                    <pre
                      style={{
                        margin: "6px 0 0",
                        fontSize: 11.5,
                        padding: 8,
                        background: "var(--bg-soft, #f1f5f9)",
                        borderRadius: 4,
                        overflowX: "auto",
                      }}
                    >
                      {JSON.stringify(e.payload, null, 2)}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
    </AdminShell>
  );
}
